<template lang="pug">
.check-delete-modal
  b-modal(v-model="_value" size="lg" no-close-on-backdrop no-close-on-esc hide-header
    @ok="ok" @cancel="cancel")
    .check-delete-modal-body
      .confirm-title {{ title }}
      .confirm.mb-3
        slot(name="message")
        label
          input(type="checkbox" @click="checked = !checked")
          span 確認しました

      .confirm.register-body.mb-3(v-if="showSendDeleteMessage")
        FormInputRadio(v-model="_sendMessage" key-name="ご家族への削除メッセージを送信するか否か" name="send-message"
          :options="_sendMessageOptions" :validation-rules="{ required: true }" required)
          template(v-slot:question)
            HelpIcon(popoverId="send-message-text" title="ご家族への削除メッセージを送信するか否かについて")
              template(v-slot:text)
                div
                  strong 送信する
                  span の場合はLINEが登録されているご家族に登録削除メッセージを送信します。
                  br
                  strong 送信しない
                  span の場合はLINEが登録されているご家族に登録削除メッセージを送信しません。

        .message-input(v-if="sendMessage == true")
          .message 削除メッセージを入力してください
          FormInput(
            v-model="_deleteMessageTitle"
            key-name="タイトル" name="delete-message-title" type="text" placeholder="タイトル"
            :maxlength="100" :validation-rules="{ required: true, max: 100 }" :styles="{ width: '720px' }" required)
          FormTextarea(
            v-model="_deleteMessageText"
            key-name="本文" name="delete-message-text" placeholder="本文"
            :validation-rules="{ required: true, max: 1000 }" :maxlength="1000" :styles="{ maxWidth: '720px', width: '720px', minHeight: '160px' }" required)

      .confirm(v-if="0 < targetFacilities.length")
        .message 複数施設に申し込んでいる場合、他施設の申込情報も同時に削除できます。
        .warning-message 同時に申込情報を削除したい施設にチェックを入れてください。
        .facilities
          .facility(v-for="(facility, index) in targetFacilities")
            b-form-checkbox(v-model="targetFacilities[index].checked" :value="true" :unchecked-value="false") {{ facility.name }}

    template(#modal-footer="{ ok, cancel }" style="width:100%;")
      ValidationAlert.mb-2(v-model="showValidationAlert")

      .buttons.d-flex.justify-content-end
        button.btn.modal-ok-btn(v-if="checked" type="button" @click="ok()") OK
        button.btn.modal-ok-btn.disable(v-else type="button" disabled) OK
        button.btn.modal-cancel-btn(type="button" @click="cancel()") キャンセル
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import ValidationAlert from "@/mobile/basics/ValidationAlertByValidator.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ### チェックボックス付き削除確認モーダル
 */
@Component({
  components: {
    HelpIcon,
    FormInputRadio,
    FormInput,
    FormTextarea,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class CheckFamilyDeleteModal extends Vue {
  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** タイトル */
  @Prop({ required: true })
  title!: string;

  /** 削除メッセージの表示可否を表示するか否か */
  @Prop({ default: true })
  showSendDeleteMessage!: boolean;
  /** 削除メッセージの送信可否 */
  @Prop()
  sendMessage!: boolean | null;
  /** 削除メッセージタイトル */
  @Prop()
  deleteMessageTitle!: string;
  /** 削除メッセージ */
  @Prop()
  deleteMessageText!: string;

  /** 並列登録する他施設 */
  @Prop({ required: true })
  targetFacilities!: Array<TargetFacility>;

  /** 確認しました */
  checked: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _sendMessage(): boolean | null {
    return this.sendMessage;
  }

  set _sendMessage(newValue: boolean | null) {
    this.$emit("update:sendMessage", newValue);
    return;
  }

  get _sendMessageOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: "送信する" },
      { value: false, label: "送信しない" }
    ];
  }

  get _deleteMessageTitle(): string {
    return this.deleteMessageTitle;
  }

  set _deleteMessageTitle(newValue: string) {
    this.$emit("update:deleteMessageTitle", newValue);
    return;
  }

  get _deleteMessageText(): string {
    return this.deleteMessageText;
  }

  set _deleteMessageText(newValue: string) {
    this.$emit("update:deleteMessageText", newValue);
    return;
  }

  /**
   * OKボタン押下イベント
   */
  public async ok(modalEvent: any): Promise<void> {
    modalEvent.preventDefault(); // イベントバブリングを止めて、自動でモーダル非表示を止める

    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.resetInput();
      this.$emit("ok");
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /** ### 入力リセット */
  private resetInput(): void {
    this.checked = false;
    return;
  }

  /**
   * キャンセルボタン押下イベント
   */
  public cancel(): void {
    this.showValidationAlert = false;
    this.$emit("cancel");
    this.resetInput();
    return;
  }
}

export interface TargetFacility {
  /** 施設ID */
  id: number;
  /** 施設名 */
  name: string;
  /** 利用者ID */
  residentId: number;
  /** 選択したか否か */
  checked: boolean;
}
</script>
